import { useCallback, useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useUser } from "../lib/customHooks"
import { getBotList, restartAllBots, startAllBots, stopAllBots } from "../services/botApis"
import getTimeDiffInDays from "../utils/getTimeDiffInDays"
import getTimeDiffFaString from "../utils/getTimeDiffFaString"
import { APP_ROUTES } from "../utils/constants"
import actionHelper from "../lib/actionHelper"
import { toast } from "react-toastify"
import MusicBotMoreModal from "../components/MusicBots/MusicBotMoreModal"
import CreateMusicBotModal from "../components/MusicBots/CreateMusicBotModal"
import Sidebar from "../components/Sidebar"
import Table from "../components/Table"
import AutoRenewButton from "../components/MusicBots/AutoRenewButton"
import { useAuth } from "../lib/useAuth"
import { useSocket, useSocketEvent } from "../contexts/SocketContext"
import LogsModal from "../components/LogsModal"
import sidebarStore from "../store/sidebarStore"

const cols = [
    {
        name: "حالت",
        colSpan: 1,
    },
    {
        name: "وضعیت",
        colSpan: 1,
    },
    {
        name: "اسم بات",
        colSpan: 1,
    },
    {
        name: "سرور",
        colSpan: 1,
    },
    {
        name: "اسم تمپلیت",
        colSpan: 1,
    },
    {
        name: "تایپ",
        colSpan: 1,
    },
    {
        name: "تاریخ انقضا",
        colSpan: 1,
    },
    {
        name: "تمدید اتومات",
        colSpan: 1,
    },

    {
        name: "تنظیمات",
        colSpan: 1,
    },
]

const MusicBots = () => {
    const { user } = useAuth()
    const { isConnected, sendMessage } = useSocket()

    const navigate = useNavigate()
    const [botList, setBotList] = useState([])
    const [rows, setRows] = useState([])
    const [selecteds, setSelecteds] = useState([])
    const [createIsOpen, setCreateIsOpen] = useState(false)
    const [searchBar, setSearchBar] = useState("")
    const setSection = sidebarStore((state) => state.setSection)

    const searchBarChangeHandler = async (e) => {
        await new Promise((resolve) => setTimeout(resolve, 500))
        setSearchBar(e.target.value)
    }

    const toggleCreate = () => {
        setCreateIsOpen(!createIsOpen)
    }

    const createRows = (bots) => {
        return bots.map((bot) => {
            const now = new Date()
            const createdDate = new Date(bot.created)
            const expireDate = new Date(bot.expires)
            const expires = bot.expires == null ? null : getTimeDiffInDays(expireDate, now)
            bot.expString = bot.expires == null ? null : getTimeDiffFaString(expireDate, now)
            bot.crtString = getTimeDiffFaString(createdDate, now)

            let obj = {
                id: bot.id,
                state: (
                    <span className={bot.state == "active" ? "bg-blur-success" : "bg-blur-warning"}>
                        {bot.state == "active" ? "فعال" : "غیرفعال"}
                    </span>
                ),
                status: (
                    <span
                        className={
                            bot.status == "connected"
                                ? "bg-blur-success"
                                : bot.status == "connecting"
                                  ? "bg-blur-warning"
                                  : "bg-blur-danger"
                        }
                    >
                        {bot.status == "connected" ? "روشن" : bot.status == "connecting" ? "درحال اتصال" : "خاموش"}
                    </span>
                ),
                name: bot.name,
                server: bot.bot_server_port ? `${bot.bot_server_ip}:${bot.bot_server_port}` : bot.bot_server_ip,
                templateName: bot.template_name,
                type: bot.type == "youtube" ? "یوتیوب" : "عادی  ",
                expString:
                    expires !== null ? (
                        <span className={expires >= 0 ? "text-success" : "text-danger"}>{bot.expString}</span>
                    ) : (
                        <span>دائمی</span>
                    ),
                renew: <AutoRenewButton user={user} bot={bot} />,
                more: (
                    <div className="flex gap-2">
                        <MusicBotMoreModal bot={bot} user={user} />
                        <LogsModal service={{ en: "audiobot", fa: "بات موزیک" }} id={bot.id} />
                    </div>
                ),
            }
            return obj
        })
    }

    const handleBotList = useCallback((data) => {
        setBotList(data)
    }, [])

    const handleBotAdded = useCallback((newBot) => {
        setBotList((list) => [...list, newBot])
    }, [])

    const handleBotUpdated = useCallback((newData) => {
        setBotList((oldBotList) => {
            return oldBotList.map((s) => {
                return s.id == newData.id ? Object.assign(s, newData) : s
            })
        })
    }, [])

    const handleBotDeleted = useCallback((deletedId) => {
        setBotList((oldBotList) => {
            return oldBotList.filter((s) => s.id != deletedId)
        })
    }, [])

    useSocketEvent("botList", handleBotList)
    useSocketEvent("botAdded", handleBotAdded)
    useSocketEvent("botUpdated", handleBotUpdated)
    useSocketEvent("botDeleted", handleBotDeleted)

    const handleRefresh = () => {
        sendMessage("getBotList")
    }

    useEffect(() => {
        if (!isConnected) {
            return
        }

        if (user && user.can_use_bot) {
            sendMessage("getBotList")
        } else if (user && user.can_use_servers) {
            navigate(APP_ROUTES.INDEX)
        } else if (user && user.can_use_manager_bots) {
            navigate(APP_ROUTES.TS_MANAGER_BOTS)
        } else if (user) {
            toast.error("شما هیچگونه دسترسی ندارید")
            localStorage.clear()
            navigate(APP_ROUTES.SIGN_IN)
        } else {
            return
        }
    }, [isConnected])

    useEffect(() => {
        if (searchBar && searchBar !== "" && botList) {
            let searchedRows = botList.filter((bot) => {
                if (
                    !bot.name.toString().includes(searchBar) &&
                    !bot.template_name.toString().includes(searchBar) &&
                    !bot.bot_owner.toString().includes(searchBar) &&
                    !bot.bot_server_port?.toString().includes(searchBar) &&
                    !bot.bot_server_ip?.toString().includes(searchBar)
                ) {
                    return false
                } else {
                    return true
                }
            })
            setRows(createRows(searchedRows))
        } else if (botList) {
            setRows(createRows(botList))
        }
    }, [searchBar, botList])

    const multiTasking = {
        setSelecteds: setSelecteds,
        selecteds: selecteds,
        tasks: [
            {
                title: "روشن کردن",
                color: "success",
                icon: "play",
                onClick: () => {
                    if (selecteds.length != 0) {
                        actionHelper({
                            confirmation: {
                                label: "ایا مطمئن هستید بات های انتخاب شده را روشن کنید؟",
                                actionLabel: "روشن کردن",
                                color: "success",
                            },
                            callback: () => {
                                return startAllBots(selecteds.map((s) => s.id))
                            },
                            texts: { success: "استارت شد", pending: "درحال استارت" },
                        })
                    } else {
                        toast.error("باتی انتخاب نشده است")
                    }
                },
            },
            {
                title: "خاموش کردن",
                color: "danger",
                icon: "power-off",
                onClick: () => {
                    if (selecteds.length != 0) {
                        actionHelper({
                            confirmation: {
                                label: "آیا مطمئن هستید بات های انتخاب شده را خاموش کنید؟",
                                actionLabel: "خاموش کردن",
                                color: "danger",
                            },
                            callback: () => {
                                return stopAllBots(selecteds.map((s) => s.id))
                            },
                            texts: { success: "خاموش شد", pending: "درحال خاموش" },
                        })
                    } else {
                        toast.error("باتی انتخاب نشده است")
                    }
                },
            },
            {
                title: "ریستارت کردن",
                color: "border",
                icon: "repeat",
                onClick: () => {
                    if (selecteds.length != 0) {
                        actionHelper({
                            confirmation: {
                                label: "ایا مطمئن هستید بات های انتخاب شده را ریستارت کنید؟",
                                actionLabel: "ریستارت کردن",
                                color: "danger",
                            },
                            callback: () => {
                                return restartAllBots(selecteds.map((s) => s.id))
                            },
                            texts: { success: "ریستارت شد", pending: "درحال ریستارت" },
                        })
                    } else {
                        toast.error("باتی انتخاب نشده است")
                    }
                },
            },
        ],
    }

    const section = {
        title: "بات های موزیک",
        setSearchBar: searchBarChangeHandler,
        buttons: [
            {
                name: "ساخت بات جدید",
                onClick: toggleCreate,
                color: "border",
                iconName: "square-plus",
            },
            {
                name: "روشن کردن همه",
                onClick: () =>
                    actionHelper({
                        confirmation: {
                            label: "ایا مطمئن هستید همه ی بات ها را روشن کنید؟",
                            actionLabel: "روشن کردن",
                            color: "success",
                        },
                        callback: () => {
                            return startAllBots(user.token)
                        },
                        texts: { success: "روشن شد", pending: "درحال روشن کردن" },
                    }),
                color: "success",
                iconName: "play",
            },
            {
                name: "ریستارت همه",
                onClick: () =>
                    actionHelper({
                        confirmation: {
                            label: "ایا مطمئن هستید همه ی بات ها را ریستارت کنید؟",
                            actionLabel: "ریستارت کردن",
                            color: "warning",
                        },
                        callback: () => {
                            return restartAllBots(user.token)
                        },
                        texts: { success: "ریستارت شد", pending: "درحال ریستارت" },
                    }),
                color: "warning",
                iconName: "repeat",
            },
            {
                name: "خاموش کردن همه",
                onClick: () =>
                    actionHelper({
                        confirmation: {
                            label: "ایا مطمئن هستید همه ی بات ها را خاموش کنید؟",
                            actionLabel: "خاموش کردن",
                            color: "danger",
                        },
                        callback: () => {
                            return stopAllBots(user.token)
                        },
                        texts: { success: "خاموش شد", pending: "درحال خاموش" },
                    }),
                color: "danger",
                iconName: "power-off",
            },
        ],
    }

    if (!user) {
        return "Loading..."
    }
    setSection(section)

    return (
        <>
            <CreateMusicBotModal user={user} isOpen={createIsOpen} toggleModal={toggleCreate} />
            <Table cols={cols} rows={rows} multiTasking={multiTasking} handleRefresh={handleRefresh} />
        </>
    )
}

export default MusicBots
